import {Trans} from '@lingui/macro';
import React, {FC, useCallback} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useConstant} from '../../../../../srcCommon/helpers/hookHelpers';
import {StringOrTemplate} from '../../../editors/commons/stringTemplateModel';
import {getExpressionTypeIcon} from '../../../editors/expressions/expressionIcons';
import {buildStringTemplateParameter} from '../../../editors/expressions/expressionParamHelpers';
import {useExpressionsContext} from '../../../editors/expressions/expressionsContext';
import {StringLikeResults, UrlResult} from '../../../editors/expressions/infer/expressionResultModel';
import {AtomLine} from '../../../rules/ruleEditor/expressions/atomLine';
import {useOnSelectCreateExpressionRef} from '../../../rules/ruleEditor/hooks/useOnSelectCreateExpression';
import {InputStringTemplateParameter} from '../../../rules/ruleEditor/inputs/inputStringTemplateParameter';
import {RuleMultilineParameterCard} from '../../../rules/ruleEditor/ruleMultilineParameterCard';
import {appendExpressionRef} from '../../../rules/stringTemplateHelpers';

/*
 * Props.
 */

interface AutolinkerUrlBuilderCardProps {
  urlBuilder: StringOrTemplate;
  onUpdate: HandlerOf<StringOrTemplate>;
  isReadOnly: boolean;
}

/*
 * Component.
 */

export const AutolinkerUrlBuilderCard: FC<AutolinkerUrlBuilderCardProps> = (props) => {
  const theme = useUserTheme();
  const {urlBuilder, onUpdate, isReadOnly} = props;

  const {expressions} = useExpressionsContext();

  const patternParam = useConstant(() => buildStringTemplateParameter('pattern'));

  const title = useConstant(() => (
    <AtomLine>
      <span>
        <Trans id="autolinker-urlbuilder">URL</Trans>
      </span>
      <TooltipCoordinator
        iconColor={theme.greys.shade70}
        render={() => (
          <Trans id="autolinker-urlbuilder-tooltip">
            Whenever a reference is matched, automatically link it with the given URL. Must use HTTPS.
          </Trans>
        )}
      />
    </AtomLine>
  ));

  const onSelectCreateExpression = useOnSelectCreateExpressionRef(
    useCallback((ref) => onUpdate(appendExpressionRef(urlBuilder, ref)), [onUpdate, urlBuilder]),
    StringLikeResults,
  );

  return (
    <RuleMultilineParameterCard
      title={title}
      icon={<Icon name={getExpressionTypeIcon(UrlResult).regular.filled} />}
    >
      <InputStringTemplateParameter
        stringParameter={patternParam}
        value={urlBuilder}
        onValueChange={onUpdate}
        isDisabled={isReadOnly}
        expressions={expressions}
        onSelectCreateExpression={onSelectCreateExpression}
      />
    </RuleMultilineParameterCard>
  );
};
